/**
 * Der Auftrag, mit dem der Massenupload-Dialog startet, und die zwei Entscheidungen davor: was wurde abgelegt, und darf
 * diese Bestellung überhaupt einen Massenupload bekommen.
 *
 * Dieses Modul gehört zum Eintritt (`MassenuploadAblage`, `MassenuploadDialogLazy`) und nicht in den Lazy-Chunk des
 * Dialogs: es importiert deshalb nichts aus `lib/massenupload/`, und was es aus dem Bestand braucht (`hatRecht`,
 * `positionskennung`), liegt ohnehin im Hauptbündel.
 */

import { positionskennung } from "../../../../lib/positionsAnzeige.ts";
import type { Order, OrderItem } from "../../../../lib/types.ts";
import { hatRecht } from "../../../../state/berechtigungen.ts";

/** Was abgelegt wurde: Einträge des Drop-Ereignisses (Ordner und Dateien) oder eine fertige Dateiliste (Ordnerauswahl). */
export type Ablagequelle =
  | { art: "eintraege"; eintraege: FileSystemEntry[] }
  | { art: "dateien"; dateien: File[] };

export interface MassenuploadAuftrag {
  bestellung: Order;
  quelle: Ablagequelle;
  /** Die Position, auf die die Dateien gezogen wurden; `null`: der Hub erkennt sie aus den Namen. */
  festePosition: number | null;
}

/**
 * Die abgelegte Quelle. SYNCHRON im `drop`-Ereignis aufzurufen: danach sind die Items des `DataTransfer` leer, und
 * `webkitGetAsEntry()` liefert nichts mehr.
 *
 * - Mindestens ein Eintrag: die Einträge (nur sie kennen Ordner).
 * - Sonst Dateien in `files`: die Dateiliste.
 * - Sonst `null`.
 */
export function ablagequelleAusDrop(daten: DataTransfer): Ablagequelle | null {
  const eintraege: FileSystemEntry[] = [];
  for (const item of Array.from(daten.items)) {
    // Ein Item ohne Eintrag (etwa ein gezogenes Textstück) liefert `null`.
    const eintrag = item.webkitGetAsEntry();
    if (eintrag) eintraege.push(eintrag);
  }
  if (eintraege.length > 0) return { art: "eintraege", eintraege };
  const dateien = Array.from(daten.files);
  return dateien.length > 0 ? { art: "dateien", dateien } : null;
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Ob die Bestellung einen Massenupload bekommen darf: Schreiben verlangt `manage_documents`, die Positionsdokumente hängen
 * an der Bestellung per UUID (ein Entwurf oder eine Altbestellung hat keine), und mindestens eine Position muss eine
 * persistierte JTL-Kennung haben, denn nur sie lässt sich beschreiben.
 */
export function massenuploadMoeglich(bestellung: Order, rechte: ReadonlySet<string>): boolean {
  if (!hatRecht(rechte, "manage_documents")) return false;
  if (typeof bestellung.id !== "string" || !UUID.test(bestellung.id)) return false;
  const positionen: unknown = bestellung.items;
  return (
    Array.isArray(positionen) &&
    (positionen as (OrderItem | null)[]).some((position) => position !== null && positionskennung(position) !== null)
  );
}
